import React, { ReactNode, RefObject, useRef, MouseEvent, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import classnames from '../utils/classnames.tsx';
import { withIcon } from './Icon.tsx';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import Button from './Button.tsx';

export interface IBaseModalProps {
    title?: ReactNode;
    visible?: boolean;
    children: ReactNode;
    className?: string;
    innerClassName?: string;
    buttons?: ReactNode;
    header?: ReactNode;
    noHeader?: boolean;
    noCloseButton?: boolean;
    headerButtons?: ReactNode[];
    containerRef?: RefObject<Element>;
    onClose?: () => void;
    preventCloseOnOverlayClick?: boolean;
}

const CloseIcon = withIcon(faXmark);

const Modal = ({
    className,
    innerClassName,
    visible = true,
    buttons,
    headerButtons,
    title,
    header,
    noHeader,
    noCloseButton,
    onClose,
    children,
    containerRef,
    preventCloseOnOverlayClick,
}: IBaseModalProps) => {
    const [hasScroll, setHasScroll] = useState(false);

    const defaultContainerRef = useRef(document.getElementById('modal-root'));
    const modalContainerRef = containerRef || defaultContainerRef;

    const contentContainerRef = useRef(null);

    // Required for closing modal on Escape press
    const handleOpenChange = (open: boolean) => {
        if (!open) onClose?.();
    };

    const handleWrapperClick = (event: MouseEvent<HTMLDivElement>) => {
        // if not clicking on the modal itself, close the modal
        if (!preventCloseOnOverlayClick && event.target instanceof Element && !event.target.closest('.js-modal')) {
            onClose?.();
        }
    };

    useEffect(() => {
        // for better cross-browser support we will use js for handling the border visibility
        // when scroll for the content is available
        const el = contentContainerRef.current;
        if (el) {
            const checkScroll = () => {
                const scroll = el.scrollHeight > el.clientHeight;
                setHasScroll(scroll);
            };

            checkScroll();

            const resizeObserver = new ResizeObserver(checkScroll);
            resizeObserver.observe(el);

            return () => resizeObserver.disconnect();
        }
    }, [children]);

    if (!modalContainerRef?.current) return null;

    return (
        <Dialog.Root open={visible} onOpenChange={handleOpenChange}>
            <Dialog.Portal container={modalContainerRef?.current}>
                <Dialog.Overlay className="absolute inset-0 bg-[rgba(0,0,0,0.75)] overflow-auto flex flex-col justify-start items-center z-[21000]" />

                <Dialog.Content
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onPointerDownOutside={preventCloseOnOverlayClick ? (event) => event.preventDefault() : undefined}
                    className={classnames(
                        'flex justify-center-safe items-center-safe absolute top-0 left-0 w-full h-full py-[3.5rem] pointer-events-auto',
                        'js-modal' // needed for popovers to work
                    )}
                    onClick={handleWrapperClick}
                >
                    <div
                        className={classnames(
                            'flex flex-col w-full max-w-[calc(100vw-1rem)] sm:w-auto sm:min-w-[31.25rem] sm:max-w-[75rem] flex-none h-[min-content] max-h-[calc(100dvh-7rem)] bg-surface-weak rounded-xl z-[21000]',
                            className
                        )}
                    >
                        {!noHeader && !header && (
                            <div
                                className={classnames(
                                    'flex justify-between items-center gap-8 px-6 border-b border-solid border-line-weak flex-none min-h-[3.5rem]',
                                    !title && 'justify-end pb-0 border-none -mb-1'
                                )}
                            >
                                {title && (
                                    <Dialog.Title className="text-base font-semibold text-content-strong max-w-[32rem]">
                                        {title}
                                    </Dialog.Title>
                                )}

                                <div className="flex">
                                    {headerButtons}

                                    {!noCloseButton && (
                                        <div className="w-8 h-8">
                                            <Dialog.Close asChild>
                                                <Button
                                                    variant="ghost"
                                                    tabIndex={-1} // unfocusable (will autofocus the first child input instead)
                                                    className="w-8 h-8 -mr-4 p-0 flex items-center justify-center text-content-strong"
                                                    onClick={onClose}
                                                >
                                                    <CloseIcon className="w-5 h-5" />
                                                </Button>
                                            </Dialog.Close>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {header && <div className="flex-none">{header}</div>}

                        <div
                            ref={contentContainerRef}
                            className={classnames(
                                'w-full h-[5rem] flex flex-col gap-2 overflow-auto flex-1 px-6 pt-6 text-base text-content-strong',
                                hasScroll && buttons && 'border-b border-line-weak pb-6',
                                innerClassName
                            )}
                        >
                            {children}
                        </div>

                        {buttons && <div className="flex gap-2 justify-end p-6 rounded-b-xl">{buttons}</div>}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default Modal;
